import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { LogoMark, API } from "@/assets";
import { CheckCircle2, Download, FileText, Loader2, XCircle, CalendarCheck } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const Shell = ({ children }) => (
  <main className="min-h-screen bg-obsidian text-ice flex items-center justify-center px-4 py-16" data-testid="payment-result-page">
    <div className="w-full max-w-lg">
      <Link to="/" className="flex items-center justify-center gap-3 mb-10" data-testid="payment-result-home-link">
        <LogoMark className="w-10 h-10" />
        <span className="font-display font-bold text-white text-sm">DGE: Cold-Blooded Records LLC™</span>
      </Link>
      {children}
    </div>
  </main>
);

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ phase: "checking", data: null });

  useEffect(() => {
    if (!sessionId) {
      setState({ phase: "error", data: null });
      return;
    }
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      try {
        const res = await fetch(`${API}/payments/status/${sessionId}`);
        if (!res.ok) throw new Error("not found");
        const data = await res.json();
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setState({ phase: "paid", data });
        } else if (attempts++ < 15) {
          setTimeout(poll, 2000);
        } else {
          setState({ phase: "pending", data });
        }
      } catch {
        if (!cancelled) setState({ phase: "error", data: null });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  const { phase, data } = state;

  return (
    <Shell>
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="bg-charcoal border border-venom/40 rounded-2xl p-8 sm:p-10 text-center glow-venom"
      >
        {phase === "checking" && (
          <div data-testid="payment-status-checking">
            <Loader2 size={34} className="mx-auto animate-spin text-venom mb-5" />
            <h1 className="font-display text-xl font-bold text-white">Confirming your payment…</h1>
            <p className="mt-2 text-sm text-ice/55">Hold tight, we&apos;re verifying with Stripe.</p>
          </div>
        )}

        {phase === "paid" && data?.track_title && (
          <div data-testid="payment-status-paid">
            <div className="mx-auto w-16 h-16 rounded-full bg-venom/15 border border-venom/50 flex items-center justify-center mb-5">
              <CheckCircle2 size={30} className="text-venom" />
            </div>
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-venom">Order Confirmed</p>
            <h1 className="mt-2 font-display text-xl sm:text-2xl font-bold text-white">
              &quot;{data.track_title}&quot; is yours.
            </h1>
            <p className="mt-2 text-sm text-ice/60">
              {data.item_name} · ${data.amount?.toFixed(2)} {String(data.currency || "usd").toUpperCase()}
            </p>
            <p className="mt-3 text-xs text-ice/45 leading-relaxed">
              A confirmation email with these same links is on its way to your inbox.
              Don&apos;t forget to credit &quot;(Prod. by Heather Da Boa)&quot; on all platforms.
            </p>
            <p className="mt-2 text-[11px] text-ice/40">
              Questions about your order? <a href="mailto:support@cold-blooded-records.com" className="text-crystal hover:text-venom transition-colors" data-testid="order-support-link">support@cold-blooded-records.com</a>
            </p>
            <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href={`${BACKEND}${data.download_url}`}
                className="inline-flex items-center justify-center gap-2 bg-venom text-obsidian font-bold text-sm px-6 py-3.5 rounded-full hover:scale-[1.03] active:scale-95 transition-all duration-300"
                data-testid="download-beat-button"
              >
                <Download size={16} /> Download Beat Files
              </a>
              <a
                href={`${BACKEND}${data.license_url}`}
                className="inline-flex items-center justify-center gap-2 border border-crystal/60 text-ice font-semibold text-sm px-6 py-3.5 rounded-full hover:bg-crystal/15 transition-all duration-300"
                data-testid="download-license-button"
              >
                <FileText size={16} /> License Agreement
              </a>
            </div>
          </div>
        )}

        {phase === "paid" && data?.booking && (
          <div data-testid="payment-status-booking-paid">
            <div className="mx-auto w-16 h-16 rounded-full bg-venom/15 border border-venom/50 flex items-center justify-center mb-5">
              <CalendarCheck size={30} className="text-venom" />
            </div>
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-venom">Booking Confirmed</p>
            <h1 className="mt-2 font-display text-xl sm:text-2xl font-bold text-white">You&apos;re on the books.</h1>
            <p className="mt-3 text-sm text-ice/60">{data.booking.label}</p>
            <p className="mt-1 font-mono text-xs text-ice/45">{data.booking.date} · {data.booking.time_slot}</p>
            <p className="mt-4 text-xs text-ice/45 leading-relaxed">
              Confirmation email sent. The DGE team will reach out within 24–48 hours to lock in the details.
            </p>
          </div>
        )}

        {phase === "pending" && (
          <div data-testid="payment-status-pending">
            <Loader2 size={30} className="mx-auto animate-spin text-crystal mb-5" />
            <h1 className="font-display text-xl font-bold text-white">Payment processing</h1>
            <p className="mt-2 text-sm text-ice/55 leading-relaxed">
              Your payment is still being confirmed. You&apos;ll receive an email with your files
              the moment it clears — usually within a few minutes.
            </p>
          </div>
        )}

        {phase === "error" && (
          <div data-testid="payment-status-error">
            <XCircle size={30} className="mx-auto text-red-400 mb-5" />
            <h1 className="font-display text-xl font-bold text-white">We couldn&apos;t verify this order</h1>
            <p className="mt-2 text-sm text-ice/55 leading-relaxed">
              If you completed payment, your files will arrive by email. For order help or general
              support, reach us at support@cold-blooded-records.com and we&apos;ll sort it out.
            </p>
          </div>
        )}

        <Link
          to="/"
          className="mt-8 inline-block font-mono text-[11px] uppercase tracking-[0.2em] text-ice/45 hover:text-venom transition-colors"
          data-testid="payment-result-back-link"
        >
          ← Back to the site
        </Link>
      </motion.div>
    </Shell>
  );
}

export function PaymentCancel() {
  return (
    <Shell>
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="bg-charcoal border border-crystal/40 rounded-2xl p-8 sm:p-10 text-center"
        data-testid="payment-cancel-card"
      >
        <XCircle size={30} className="mx-auto text-crystal mb-5" />
        <h1 className="font-display text-xl font-bold text-white">Checkout canceled</h1>
        <p className="mt-3 text-sm text-ice/55 leading-relaxed">
          No charge was made. The beat is still in the store whenever you&apos;re ready.
        </p>
        <Link
          to="/"
          className="mt-7 inline-flex items-center gap-2 bg-venom text-obsidian font-bold text-sm px-7 py-3.5 rounded-full hover:glow-venom transition-all duration-300"
          data-testid="payment-cancel-back-button"
        >
          Back to the Beat Store
        </Link>
      </motion.div>
    </Shell>
  );
}
